/**
 * Hook owning the metadata + mp4 + blob URL lifecycle for a finalized
 * browser session.
 *
 * Returns one of three statuses:
 *   - `pending`   — metadata fetch in flight, polling for finalize, or
 *                   blob fetch in flight.
 *   - `ready`     — videoUrl is a playable blob URL ready to bind to
 *                   `<video src>`.
 *   - `unavailable` — terminal failure: server signaled permanent
 *                   unavailability, the poll ceiling fired, the recording
 *                   has no filename (zero-action session), or the blob
 *                   fetch failed.
 *
 * The caller (BrowserRenderer) uses this to decide whether to render
 * BrowserVideoView (status === 'ready') or fall back to ScreencastViewer's
 * terminal state (any other status). The fallback to "last frame +
 * session ended" is therefore implicit — when this hook returns
 * `unavailable`, the player simply never swaps to REPLAY.
 *
 * Polling lifecycle: 5s interval, 3-min ceiling. Stops immediately if
 * useRecordingMetadata throws a {@link RecordingUnavailableError}
 * (permanent failure mode introduced by the artifacts-mount-detection
 * server change), saving 35 wasted GETs in the rollout window.
 *
 * @module browser-automation/hooks/use-replay-video
 */

import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';

import {
  isRecordingUnavailableError,
  useRecordingMetadata,
} from './use-recording-metadata';

/** 3-minute ceiling for metadata polling; longer recordings give up. */
const POLL_TIMEOUT_MS = 180_000;
const POLL_INTERVAL_MS = 5_000;
/** Keeps the downloaded mp4 around so collapse/expand or maximize doesn't re-download it. */
const VIDEO_CACHE_MS = 5 * 60 * 1000;
const VIDEO_QUERY_ROOT = 'browser-recording-video';

export type ReplayVideoStatus = 'pending' | 'ready' | 'unavailable';

export interface IUseReplayVideoReturn {
  status: ReplayVideoStatus;
  /** Blob URL bound to `<video src>` when status === 'ready'. */
  videoUrl: string | null;
}

/**
 * Build the React Query key for a session's recording blob. Exported so
 * callers (and tests) can read or invalidate the cached video.
 */
export function recordingVideoQueryKey(
  artifactsBase: string | null,
  filename: string | undefined,
): readonly [string, string, string] {
  return [VIDEO_QUERY_ROOT, artifactsBase ?? '', filename ?? ''];
}

/**
 * @param artifactsBase Resolved artifacts URL (output of
 *   `resolveArtifactsBaseUrl`). Pass `null` to keep the hook idle (no
 *   metadata fetch, status stays `pending`).
 */
export function useReplayVideo(
  artifactsBase: string | null,
): IUseReplayVideoReturn {
  const { getAccessTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
    refetch: refetchMetadata,
  } = useRecordingMetadata(artifactsBase);

  const [pollExpired, setPollExpired] = useState(false);

  // Auth0 returns a fresh closure each render; ref keeps it stable.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;

  // Reset state when the underlying session changes.
  // eslint-disable-next-line no-restricted-syntax -- reason: Reset derived state across session changes
  useEffect(() => {
    setPollExpired(false);
  }, [artifactsBase]);

  // Permanent failure short-circuit. RecordingUnavailableError is thrown
  // by useRecordingMetadata for 503 (mount missing) or 404 with the
  // FastAPI default body (endpoint missing). No polling, no retry.
  const permanentFailure =
    metadataError !== null &&
    metadataError !== undefined &&
    isRecordingUnavailableError(metadataError);

  // Poll loop: refetch every 5s while metadata.json returns 404, up to
  // POLL_TIMEOUT_MS. Stops immediately on permanent failure or any
  // other error.
  // eslint-disable-next-line no-restricted-syntax -- reason: Polling lifecycle with abort/cleanup
  useEffect(() => {
    if (!artifactsBase) return;
    if (metadataLoading) return;
    if (metadata !== null && metadata !== undefined) return;
    if (metadataError) return;
    if (pollExpired) return;

    const startTime = Date.now();
    let cancelled = false;
    const pollTimers = new Set<ReturnType<typeof setTimeout>>();

    function schedule(): void {
      if (cancelled) return;
      if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
        setPollExpired(true);
        return;
      }
      const timer = setTimeout(() => {
        if (cancelled) return;
        void refetchMetadata().then(result => {
          if (cancelled) return;
          if (result.data !== null && result.data !== undefined) return;
          if (result.error) return;
          schedule();
        });
      }, POLL_INTERVAL_MS);
      pollTimers.add(timer);
    }

    schedule();

    return () => {
      cancelled = true;
      pollTimers.forEach(clearTimeout);
      pollTimers.clear();
    };
  }, [
    artifactsBase,
    metadata,
    metadataLoading,
    metadataError,
    pollExpired,
    refetchMetadata,
  ]);

  // Zero-action session or schema change that dropped the field —
  // route through 'unavailable' rather than fetching `${base}/undefined`.
  const recordingFilename = metadata?.recording?.filename;
  const metadataMissingFilename =
    metadata !== null && metadata !== undefined && !recordingFilename;

  // The mp4 is immutable once metadata names it, so the Blob is cached by
  // React Query and survives remounts; each mount only mints its own object URL.
  const { data: videoBlob, error: blobError } = useQuery<Blob, Error>({
    queryKey: recordingVideoQueryKey(artifactsBase, recordingFilename),
    enabled: Boolean(artifactsBase && recordingFilename),
    staleTime: Infinity,
    gcTime: VIDEO_CACHE_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    // No abort signal on purpose: a collapse mid-download should still land in the cache.
    queryFn: async ({ queryKey }) => {
      const headers: Record<string, string> = {};
      if (isE2ETestMode()) {
        headers['X-E2E-Test-Mode'] = 'true';
      } else {
        const token = await getTokenRef.current();
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(`${artifactsBase}/${recordingFilename}`, {
        headers,
      });
      if (!res.ok) {
        throw new Error(`recording fetch failed: ${res.status}`);
      }
      const blob = await res.blob();
      // Bound memory to one off-screen recording: drop finished videos no player is showing.
      const currentHash = hashKey(queryKey);
      queryClient.removeQueries({
        queryKey: [VIDEO_QUERY_ROOT],
        predicate: query =>
          query.queryHash !== currentHash &&
          query.getObserversCount() === 0 &&
          query.state.fetchStatus !== 'fetching',
      });
      return blob;
    },
  });

  const [blobUrl, setBlobUrl] = useState<{ blob: Blob; url: string } | null>(
    null,
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: Object URLs need explicit create/revoke
  useEffect(() => {
    if (!videoBlob) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(videoBlob);
    setBlobUrl({ blob: videoBlob, url });
    return () => URL.revokeObjectURL(url);
  }, [videoBlob]);

  // Ignore a URL minted for a previous session's blob during the render before the effect re-runs.
  const videoUrl = blobUrl && blobUrl.blob === videoBlob ? blobUrl.url : null;

  const status = deriveStatus({
    artifactsBase,
    permanentFailure,
    metadataError,
    blobError,
    videoUrl,
    metadataMissingFilename,
    pollExpired,
    metadataNull: metadata === null,
  });

  return { status, videoUrl };
}

interface IStatusInputs {
  artifactsBase: string | null;
  permanentFailure: boolean;
  metadataError: Error | null;
  blobError: Error | null;
  videoUrl: string | null;
  metadataMissingFilename: boolean;
  pollExpired: boolean;
  metadataNull: boolean;
}

/**
 * Pure derivation of the public status from internal state. (Not a
 * hook — just collected into a function for readability.)
 *
 * `pending` covers both initial load and active polling — both are
 * "we're still trying, render the screencast terminal state."
 *
 * `unavailable` covers all terminal-failure shapes: permanent server
 * signal, any other error, missing filename, poll ceiling, blob fetch
 * failure. The caller treats them identically.
 */
function deriveStatus(s: IStatusInputs): ReplayVideoStatus {
  if (!s.artifactsBase) return 'pending';
  if (s.videoUrl) return 'ready';
  if (
    s.permanentFailure ||
    s.metadataError ||
    s.blobError ||
    s.metadataMissingFilename ||
    (s.metadataNull && s.pollExpired)
  ) {
    return 'unavailable';
  }
  return 'pending';
}
